
import React from "react";
import { Pencil, Trash2, Battery, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import { ComponentBike } from "../utils/bikeMappers";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

interface BikesListProps {
  bikes: ComponentBike[];
  isLoading: boolean;
  currentPage: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onDeleteClick: (bikeId: string, bikeName: string) => void;
}

const BikesList: React.FC<BikesListProps> = ({
  bikes,
  isLoading,
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onDeleteClick,
}) => {
  const totalPages = Math.ceil(totalItems / pageSize);
  
  const handleDeleteClick = (bikeId: string, bikeName: string) => {
    onDeleteClick(bikeId, bikeName);
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array(6).fill(0).map((_, index) => (
          <Card key={index} className="overflow-hidden">
            <CardContent className="p-0">
              <div className="p-6 space-y-4">
                <Skeleton className="h-6 w-3/4" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-2/3" />
                </div>
                <div className="flex justify-between">
                  <Skeleton className="h-8 w-20" />
                  <Skeleton className="h-8 w-20" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {bikes.map((bike) => (
          <Card key={bike.id} className="overflow-hidden">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="font-semibold text-lg">{bike.model}</h3>
                  <div className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                    bike.status === "active" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                  }`}>
                    {bike.status}
                  </div>
                </div>
                
                <div className="text-sm text-gray-500 space-y-1">
                  <p>Serial: {bike.serialNumber}</p>
                  <p>Location: {bike.location}</p>
                  <p>Distance: {bike.distanceTraveled} km</p>
                </div>
                
                <div className="flex items-center text-sm">
                  <Battery className="h-4 w-4 mr-1" />
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full ${
                        bike.batteryLevel > 70 ? "bg-green-500" : 
                        bike.batteryLevel > 30 ? "bg-yellow-500" : "bg-red-500"
                      }`} 
                      style={{ width: `${bike.batteryLevel}%` }}
                    />
                  </div>
                  <span className="ml-2">{bike.batteryLevel}%</span>
                </div>
                
                <div className="flex justify-between gap-2">
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/bikes/${bike.id}`}>
                      <Eye className="h-4 w-4 mr-2" />
                      View
                    </Link>
                  </Button>
                  <Button variant="outline" size="sm" asChild>
                    <Link to={`/bikes/${bike.id}/edit`}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </Link>
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-destructive hover:bg-destructive/10"
                    onClick={() => handleDeleteClick(bike.id, bike.model)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        
        {bikes.length === 0 && (
          <div className="col-span-full flex justify-center items-center py-12 text-gray-500">
            No eBikes found
          </div>
        )}
      </div>
      
      {totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                  className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink
                    isActive={page === currentPage}
                    onClick={() => onPageChange(page)}
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                  className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};

export default BikesList;
